import { Controller } from '@hotwired/stimulus';
import { csrfHeaders, ensureFormCsrfToken, markInputValid, markInputInvalid } from '@/services/form.services';
import {openDrawer} from "@/services/drawer-service";


declare global {
    interface Window {
        signupFormRecaptchaCallback?: (token: string) => void;
        signupFormRecaptchaExpired?: () => void;
        grecaptcha?: unknown;
    }
}

export class SignupFormController extends Controller {
    private static readonly unavailableButtonClasses = ['bg-zinc-500/33', 'cursor-not-allowed'];
    private static readonly submittingButtonClasses = ['cursor-wait'];

    declare readonly formTarget: HTMLFormElement;
    declare readonly nameTarget: HTMLInputElement;
    declare readonly nameErrorTargets: HTMLInputElement[];
    declare readonly emailTarget: HTMLInputElement;
    declare readonly emailErrorTargets: HTMLInputElement[];
    declare readonly passwordTarget: HTMLInputElement;
    declare readonly passwordErrorTargets: HTMLInputElement[];
    declare readonly termsCheckboxTarget: HTMLInputElement;
    declare readonly submitButtonTarget: HTMLButtonElement;
    declare readonly submitButtonTextTarget: HTMLElement;
    declare readonly submitButtonLoadingTarget: HTMLElement;
    declare readonly recaptchaTarget: HTMLElement;
    declare readonly hasRecaptchaTarget: boolean;
    declare readonly recaptchaWarningTarget: HTMLElement;
    declare readonly hasRecaptchaWarningTarget: boolean;
    declare readonly planInputTargets: HTMLInputElement[];
    declare readonly planCardTargets: HTMLElement[];

    static targets = [
        "form",
        "name",
        'nameError',
        "email",
        'emailError',
        "password",
        'passwordError',
        "referralCode",
        "termsCheckbox",
        "submitButton",
        "submitButtonText",
        "submitButtonLoading",
        "planInput",
        "planCard",
        'recaptcha',
        'recaptchaWarning'
    ];

    declare readonly recaptchaSiteKeyValue: string;
    declare readonly submitUrlValue: string;

    static values = {
        recaptchaSiteKey: String,
        submitUrl: String,
    };

    recaptchaToken: string | null = null;
    private recaptchaFallbackMode = false;
    private recaptchaReadyCheckTimer: number | null = null;
    private submitRecoveryTimer: number | null = null;
    private isSubmitting = false;

    connect = () => {
        this.element.addEventListener('turbo:submit-end', this.handleSubmitEnd);
        window.addEventListener('pageshow', this.handlePageShow);
        window.addEventListener('focus', this.handlePageFocus);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        this.initializeRecaptcha();
        this.syncPlanSelection();
        this.updateSubmitButtonState();
    }

    disconnect = () => {
        this.element.removeEventListener('turbo:submit-end', this.handleSubmitEnd);
        window.removeEventListener('pageshow', this.handlePageShow);
        window.removeEventListener('focus', this.handlePageFocus);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        if (this.recaptchaReadyCheckTimer) {
            window.clearTimeout(this.recaptchaReadyCheckTimer);
            this.recaptchaReadyCheckTimer = null;
        }
        if (this.submitRecoveryTimer) {
            window.clearTimeout(this.submitRecoveryTimer);
            this.submitRecoveryTimer = null;
        }
    }

    handleSubmitEnd = () => {
        this.hideLoadingState();
    }

    handlePageShow = () => {
        this.hideLoadingState();
    }

    handlePageFocus = () => {
        this.hideLoadingState();
    }

    handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            this.hideLoadingState();
        }
    }

    // Stimulus action for terms checkbox change
    termsChanged = () => {
        this.updateSubmitButtonState();
    }

    selectPlan = (event: Event) => {
        const trigger = event.currentTarget as HTMLElement | null;
        const selectedPlan = trigger?.dataset.planId;
        if (!selectedPlan) {
            return;
        }

        for (const input of this.planInputTargets) {
            input.checked = input.value === selectedPlan;
        }

        this.syncPlanSelection();
    }

    planChanged = () => {
        this.syncPlanSelection();
    }

    updateSubmitButtonState = () => {
        const isTermsChecked = this.termsCheckboxTarget.checked;
        const isAvailable = isTermsChecked;

        if (this.isSubmitting) {
            this.submitButtonTarget.disabled = true;
            this.submitButtonTarget.classList.remove(...SignupFormController.unavailableButtonClasses);
            this.submitButtonTarget.classList.add(...SignupFormController.submittingButtonClasses);
            return;
        }

        this.submitButtonTarget.disabled = !isAvailable;
        this.submitButtonTarget.classList.remove(...SignupFormController.submittingButtonClasses);

        if (isAvailable) {
            this.submitButtonTarget.classList.remove(...SignupFormController.unavailableButtonClasses);
        } else {
            this.submitButtonTarget.classList.add(...SignupFormController.unavailableButtonClasses);
        }
    }

    // reCAPTCHA integration
    initializeRecaptcha = () => {
        if (!this.requiresRecaptcha()) {
            return;
        }

        // Set up global callback references for reCAPTCHA
        window.signupFormRecaptchaCallback = this.onRecaptchaSuccess.bind(this);
        window.signupFormRecaptchaExpired = this.onRecaptchaExpired.bind(this);
        this.ensureRecaptchaScript();
        this.scheduleRecaptchaReadyCheck();
    }

    hasRecaptchaElement = () => {
        return this.hasRecaptchaTarget;
    }

    onRecaptchaSuccess = (token: string) => {
        this.recaptchaToken = token;
        this.updateSubmitButtonState();
    }

    onRecaptchaExpired = () => {
        this.recaptchaToken = null;
        this.updateSubmitButtonState();
    }

    isRecaptchaCompleted = () => {
        // If no site key is provided, reCAPTCHA is not required
        if (!this.recaptchaSiteKeyValue) {
            return true;
        }
        return this.recaptchaToken != null;
    }

    // Form submission
    submit = async (event?: Event) => {
        if (this.isSubmitting) {
            event?.preventDefault();
            return false;
        }

        if (!this.validateBasicForm()) {
            event?.preventDefault();
            return false;
        }

        if (this.requiresRecaptcha() && !this.isRecaptchaCompleted()) {
            event?.preventDefault();
            this.setRecaptchaWarning('Please complete reCAPTCHA verification before continuing.');
            return false;
        }

        if (event?.type === 'submit') {
            event.preventDefault();
            this.showLoadingState();
            await this.submitWithFetch();
            return false;
        }

        event?.preventDefault();
        this.formTarget.requestSubmit();
    }

    private submitWithFetch = async () => {
        ensureFormCsrfToken(this.formTarget);

        try {
            const response = await fetch(this.formTarget.action || this.submitUrlValue, {
                method: this.formTarget.method || 'POST',
                body: new FormData(this.formTarget),
                credentials: 'same-origin',
                headers: {
                    ...csrfHeaders(),
                    'X-Signup-Async': '1',
                },
            });

            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                const payload = await response.json() as { next_url?: string; error?: string };
                if (payload.next_url) {
                    window.location.assign(payload.next_url);
                    return;
                }
                if (payload.error) {
                    this.hideLoadingState();
                    this.setRecaptchaWarning(payload.error);
                    return;
                }
            }

            const html = await response.text();
            if (html) {
                const parsed = new DOMParser().parseFromString(html, 'text/html');
                const replacement = document.importNode(parsed.documentElement, true);
                document.documentElement.replaceWith(replacement);
                return;
            }

            this.hideLoadingState();
        } catch (error) {
            console.error('signup submit failed', error);
            this.hideLoadingState();
            alert('Signup could not continue. Please try again.');
        }
    }

    validateBasicForm = () => {
        // Basic validation - terms must be checked
        if (!this.termsCheckboxTarget.checked) {
            alert("Please accept the Terms of Service and Privacy Policy");
            return false;
        }

        // Run individual field validations
        const nameValid = this.validateName();
        const emailValid = this.validateEmail();
        const passwordValid = this.validatePassword();

        // Check if all validations pass
        return !(!nameValid || !emailValid || !passwordValid);


    }

    showLoadingState = () => {
        this.isSubmitting = true;
        this.submitButtonTarget.setAttribute('aria-busy', 'true');
        this.submitButtonTextTarget.classList.add('hidden');
        this.submitButtonLoadingTarget.classList.remove('hidden');
        this.submitButtonLoadingTarget.classList.add('inline-flex');
        this.submitRecoveryTimer = window.setTimeout(() => {
            this.hideLoadingState();
        }, 12000);
        this.updateSubmitButtonState();
    }

    hideLoadingState = () => {
        if (this.submitRecoveryTimer) {
            window.clearTimeout(this.submitRecoveryTimer);
            this.submitRecoveryTimer = null;
        }
        this.isSubmitting = false;
        this.submitButtonTarget.removeAttribute('aria-busy');
        this.submitButtonLoadingTarget.classList.add('hidden');
        this.submitButtonLoadingTarget.classList.remove('inline-flex');
        this.submitButtonTextTarget.classList.remove('hidden');
        this.updateSubmitButtonState();
    }

    validateName = () => {
        if (this.nameTarget.value.trim().length < 2) {
            markInputInvalid(this.nameTarget, this.nameErrorTargets);
            this.nameTarget.addEventListener('input', this.validateName);

            return false;
        } else {
            markInputValid(this.nameTarget, this.nameErrorTargets);
            this.nameTarget.removeEventListener('input', this.validateName);

            return true;
        }
    }

    validateEmail = () => {
        if (!this.emailTarget.validity.valid) {
            markInputInvalid(this.emailTarget, this.emailErrorTargets);
            this.emailTarget.addEventListener('input', this.validateEmail);

            return false;
        } else {
            markInputValid(this.emailTarget, this.emailErrorTargets);
            this.emailTarget.removeEventListener('input', this.validateEmail);

            return true;
        }
    }

    validatePassword = () => {
        if (this.passwordTarget.value.trim().length < 8) {
            markInputInvalid(this.passwordTarget, this.passwordErrorTargets);
            this.passwordTarget.addEventListener('input', this.validatePassword);

            return false;
        } else {
            markInputValid(this.passwordTarget, this.passwordErrorTargets);
            this.passwordTarget.removeEventListener('input', this.validatePassword);

            return true;
        }
    }

    openTerms() {
        openDrawer('terms-of-service-drawer');
    }

    openPrivacyPolicy() {
        openDrawer('privacy-policy-drawer');
    }

    private syncPlanSelection(): void {
        const selectedValue = this.planInputTargets.find((input) => input.checked)?.value;

        for (const card of this.planCardTargets) {
            const isSelected = card.dataset.planId === selectedValue;
            card.classList.toggle('border-green-600', isSelected);
            card.classList.toggle('bg-green-50', isSelected);
            card.classList.toggle('ring-2', isSelected);
            card.classList.toggle('ring-green-200', isSelected);
            card.classList.toggle('border-zinc-200', !isSelected);
        }
    }

    private requiresRecaptcha(): boolean {
        return Boolean(this.recaptchaSiteKeyValue) && this.hasRecaptchaElement();
    }

    private ensureRecaptchaScript(): void {
        if (document.querySelector('script[src*="recaptcha/api.js"]')) {
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }

    private scheduleRecaptchaReadyCheck(): void {
        if (this.recaptchaReadyCheckTimer) {
            window.clearTimeout(this.recaptchaReadyCheckTimer);
        }

        this.recaptchaReadyCheckTimer = window.setTimeout(() => {
            this.enableRecaptchaFallbackIfNeeded();
            this.recaptchaReadyCheckTimer = null;
        }, 2500);
    }

    private isHotwireNativeWebView(): boolean {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('hotwire native') || ua.includes('turbo native');
    }

    private enableRecaptchaFallbackIfNeeded(): void {
        if (!this.requiresRecaptcha() || !this.isHotwireNativeWebView() || typeof window.grecaptcha !== 'undefined') {
            return;
        }

        this.recaptchaFallbackMode = true;
        this.recaptchaToken = null;
        this.setRecaptchaWarning(
            'reCAPTCHA did not load in this in-app view. Open this page in your browser to complete signup.'
        );
        this.updateSubmitButtonState();
    }

    private setRecaptchaWarning(message: string): void {
        if (!this.hasRecaptchaWarningTarget) {
            return;
        }

        this.recaptchaWarningTarget.textContent = message;
        this.recaptchaWarningTarget.classList.remove('hidden');
    }
}
